import { AxiosError } from 'axios';
import { useMutation } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
	getChangingModelText,
	getCustomModelFileUrl,
	getModelFile,
	retryImportModelFile,
} from 'entities/CustomModel/api';
import { PageWrapper } from 'pages/_layouts/PageWrapper';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import FileViewer from 'components/file-editor-components/file-viewer';
import EditorWrapper from 'components/file-editor-components/editor-wrapper';
import LabelFields from 'components/file-editor-components/label-fields';
import TableFields from 'components/file-editor-components/table-fields';
import FieldLabelModal from 'components/file-editor-components/field-label-modal';
import { useFileEditor } from 'components/file-editor-components/useFileEditor';

const CustomFileEditor = () => {
	const { modelId } = useParams();
	const navigate = useNavigate();

	const {
		fileDetails,
		fileUrl,
		isLoading,
		fileViewerRef,
		pendingRegion,
		setPendingRegion,
		drawMode,
		setDrawMode,
		pendingCellTarget,
		pendingLabelTarget,
		regions,
		setRegions,
		selectedSourceId,
		setSelectedSourceId,
		updateLabelContent,
		handleCancelLabel,
		handleSaveLabel,
		handleCellTargetClick,
		handleLabelTargetClick,
		handleCellContentChange,
		handleCellCandidateSelect,
		handleDeleteLabel,
		handleCellDelete,
		handleRegionCreated,
		handleRegionUpdated,
		handleDeleteTable,
		handleAddRow,
		handleAddTable,
		handleUpdateTable,
		handleDeleteRow,
		handleDeleteColumn,
		handleReorderColumns,
		getColumnPinPreferences,
		handleTogglePinColumn,
	} = useFileEditor({
		entityId: modelId,
		detailsQueryKey: ['get/modelById_v2'],
		urlQueryKey: ['get/customModelFileUrl_v2', modelId],
		regionTextQueryKey: 'get-custom/rectangle-content/cell',
		getDetails: getModelFile,
		getUrl: getCustomModelFileUrl,
		getRegionText: getChangingModelText,
	});

	const { mutate: modelMutation, isLoading: isSaving } = useMutation({
		mutationKey: ['put/custom-models/file-labels_v2'],
		mutationFn: retryImportModelFile,
	});

	const handleSave = () => {
		if (!fileDetails) return;

		modelMutation(
			{
				customModelFileId: fileDetails._id,
				data: { labels: fileDetails.recognizedLabels },
			},
			{
				onSuccess: () => {
					navigate(-1);
				},
				onError: (err) => {
					showErrorNotification(err as AxiosError);
				},
			},
		);
	};

	return (
		<PageWrapper>
			<EditorWrapper
				onSave={handleSave}
				isSaving={isSaving}
				isLoading={isLoading}
				filePreview={
					fileUrl && (
						<FileViewer
							ref={fileViewerRef}
							url={fileUrl}
							fileName={fileDetails?.fileName || ''}
							regions={regions}
							onRegionsChange={setRegions}
							selectedSourceId={selectedSourceId}
							onSelectSource={setSelectedSourceId}
							onRegionNeedsLabel={setPendingRegion}
							onRegionCreated={handleRegionCreated}
							onRegionUpdated={handleRegionUpdated}
							onDrawModeChange={setDrawMode}
						/>
					)
				}
				sidebar={
					<>
						<LabelFields
							labels={fileDetails?.recognizedLabels || []}
							selectedSourceId={selectedSourceId}
							onSelectSource={setSelectedSourceId}
							onContentChange={updateLabelContent}
							onDeleteLabel={handleDeleteLabel}
							drawMode={drawMode}
							pendingLabelTarget={pendingLabelTarget}
							onLabelTargetClick={handleLabelTargetClick}
						/>
						<TableFields
							labels={fileDetails?.recognizedLabels || []}
							selectedSourceId={selectedSourceId}
							onSelectSource={setSelectedSourceId}
							drawMode={drawMode}
							pendingCellTarget={pendingCellTarget}
							onCellTargetClick={handleCellTargetClick}
							onCellContentChange={handleCellContentChange}
							onCellCandidateSelect={handleCellCandidateSelect}
							onCellDelete={handleCellDelete}
							onDeleteTable={handleDeleteTable}
							onAddTable={handleAddTable}
							onUpdateTable={handleUpdateTable}
							onAddRow={handleAddRow}
							onDeleteRow={handleDeleteRow}
							onDeleteColumn={handleDeleteColumn}
							onReorderColumns={handleReorderColumns}
							getColumnPinPreferences={getColumnPinPreferences}
							onTogglePinColumn={handleTogglePinColumn}
						/>
					</>
				}
			/>
			<FieldLabelModal
				fileId={modelId}
				region={pendingRegion}
				onCancel={handleCancelLabel}
				onSave={handleSaveLabel}
				mode="custom"
			/>
		</PageWrapper>
	);
};

export default CustomFileEditor;
